import { useState, useCallback, useRef } from 'react'
import type { FC } from 'react'
import type { Word } from '../../types/wordCollection'
import { NotesIcon } from './NotesIcon'
import { NotesPopover } from './NotesPopover'

interface SetAsideItemProps {
  word: Word
  onReactivate: () => void
  onUpdateNotes: (notes: string) => void
}

/**
 * Format a date as relative time (e.g., "2 days ago", "3 hours ago")
 */
function formatRelativeDate(date: Date | string | undefined): string {
  if (!date) return 'Unknown'

  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - dateObj.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Just now'
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`

  const diffMonths = Math.floor(diffDays / 30)
  if (diffMonths < 12) return `${diffMonths} month${diffMonths === 1 ? '' : 's'} ago`

  const diffYears = Math.floor(diffDays / 365)
  return `${diffYears} year${diffYears === 1 ? '' : 's'} ago`
}

/**
 * SetAsideItem - Individual set-aside word row with muted styling and reactivate button
 *
 * Displays word with relative set-aside date, optional reason, notes icon, and
 * hover-revealed reactivate button. Uses muted styling to de-emphasize set-aside words.
 */
export const SetAsideItem: FC<SetAsideItemProps> = ({
  word,
  onReactivate,
  onUpdateNotes
}) => {
  const [isHovered, setIsHovered] = useState(false)
  const [notesPopoverOpen, setNotesPopoverOpen] = useState(false)
  const [notesIconRef, setNotesIconRef] = useState<HTMLElement | null>(null)
  const notesContainerRef = useRef<HTMLDivElement>(null)

  const handleNotesClick = useCallback(() => {
    if (notesContainerRef.current) {
      // Find the actual button element inside the container
      const button = notesContainerRef.current.querySelector('button')
      if (button) {
        setNotesIconRef(button)
        setNotesPopoverOpen(true)
      }
    }
  }, [])

  const handleNotesClose = useCallback(() => {
    setNotesPopoverOpen(false)
  }, [])

  const handleNotesSave = useCallback((notes: string) => {
    onUpdateNotes(notes)
  }, [onUpdateNotes])

  const setAsideDate = word.timeline.set_aside_at
  const setAsideReason = word.timeline.set_aside_reason
  const relativeDate = formatRelativeDate(setAsideDate)

  return (
    <>
      <div
        className="group relative flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2 opacity-75 transition-all hover:opacity-100"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        data-testid={`set-aside-item-${word.id}`}
      >
        {/* Word info */}
        <div className="flex-1 min-w-0 mr-2">
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500 font-medium">
              {word.word}
            </span>
            <div className="flex items-center" ref={notesContainerRef}>
              <NotesIcon
                hasNotes={!!word.notes}
                onClick={handleNotesClick}
                disabled={false}
              />
            </div>
          </div>
          <div className="text-[0.65rem] text-gray-400 mt-0.5">
            {relativeDate}
            {setAsideReason && (
              <span className="ml-1">• {setAsideReason}</span>
            )}
          </div>
        </div>

        {/* Reactivate button - shown on hover */}
        <button
          type="button"
          onClick={onReactivate}
          className={`flex-shrink-0 px-2 py-1 text-xs bg-green-500 text-white rounded hover:bg-green-600 transition-all ${
            isHovered ? 'opacity-100' : 'opacity-0'
          }`}
          data-testid={`reactivate-button-${word.id}`}
        >
          Reactivate
        </button>
      </div>

      {/* Notes popover */}
      <NotesPopover
        isOpen={notesPopoverOpen}
        onClose={handleNotesClose}
        referenceElement={notesIconRef}
        initialNotes={word.notes || ''}
        onSave={handleNotesSave}
        entityType="word"
        entityLabel={word.word}
      />
    </>
  )
}
